import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FAVORITE_TEAM_SPORT } from 'utils/constants/sportsbook/favorite-team';
import { NOT_REAL_SPORTS } from 'utils/constants/sportsbook/sportsbook-data-hierarchy';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { PageType } from 'interfaces/generic';
import { ENotRealSports } from 'interfaces/not-real-sports';
import {
  EOlympicLayoutType,
  ETopLeagueLayoutType,
  ETopMatchesLayoutType
} from 'interfaces/sportsbook-configs';
import { FavoriteSideBarSearchSection } from './FavoriteSideBarSearchSection';
import { SportsbookNavigationsBlock } from './style';
import { TopLeagueCompactMenuItemWithWrapper } from './TopLeagueCompactMenuItemWithWrapper';
import { CategoriesNavigation } from 'newelements/Sportsbook/__partials/CategoriesNavigation';
import { SportsHeader } from 'newelements/Sportsbook/__partials/SportsHeader';
import { SportsNavigation } from 'newelements/Sportsbook/__partials/SportsNavigation';
import { TPrematchCategoryTypes } from 'newelements/Sportsbook/helpers';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { SportsbookEasyAccessNavigation } from 'components/SportsbookEasyAccessNavigation';
import { OlympicCompetitionCompactLayout } from 'newcomponents/Shared/sportsbook/OlympicCompetitionCompactLayout';
import { TopMatchesCompactMenuItem } from 'newcomponents/Shared/sportsbook/TopMatchesCompactMenuItem';
import { VerticalSportsList } from 'newcomponents/Shared/sportsbook/VerticalSportsList';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { getSportsData } from 'store/selectors/new-sportsbook';
import './index.less';

type TSportsbookLeftSideBarProps = {
  configs: {
    pageType: PageType;
    easyAccessAvailable: boolean;
    routes?: Record<keyof PageType, string>;
    onlyCounts?: boolean;
    category?: string | boolean;
    showCompetitionsList?: boolean;
    favoriteTeam?: boolean;
    sportType?: string;
  };
} & RouteComponentProps;

const sportsbookLeftSideBarDefaultProps = {
  configs: {
    pageType: 'blank',
    easyAccessAvailable: false,
    onlyCounts: false,
    category: false,
    showCompetitionsList: false,
    favoriteTeam: false,
    sportType: '',
    routes: undefined
  }
} as TSportsbookLeftSideBarProps;

const _SportsbookLeftSideBar = ({
  configs
}: TSportsbookLeftSideBarProps = sportsbookLeftSideBarDefaultProps): ReactElement => {
  const pageConfig = useSportsbookConfigs();
  const routeParams = useSbRouteMatch();
  const sportsData = useSelector(getSportsData);
  const { t } = useTranslation();

  const isBoostedOdds =
    routeParams.params?.category === ENotRealSports.boostedOdds ||
    routeParams.params?.sport === ENotRealSports.boostedOdds;

  const showTopLeague =
    configs.pageType !== 'live' &&
    SpringConfigs.TOP_LEAGUES_LAYOUT === ETopLeagueLayoutType.compact &&
    SpringConfigs.TOP_LEAGUES;

  const showTopMatches =
    configs.pageType !== 'live' &&
    SpringConfigs.TOP_MATCHES_LAYOUT === ETopMatchesLayoutType.compact &&
    SpringConfigs.TOP_MATCHES;

  const showOlympicCompetition =
    configs.pageType !== 'live' &&
    SpringConfigs.OLYMPIC_COMPETITION_LAYOUT === EOlympicLayoutType.compact &&
    SpringConfigs.OLYMPIC_COMPETITION;

  const NavigationBlock = useMemo(() => {
    if (configs.onlyCounts) {
      return null;
    }

    if (isBoostedOdds) {
      return (
        <VerticalSportsList
          sportCategory={
            (routeParams.params?.category ||
              routeParams.params?.sport) as ENotRealSports
          }
        />
      );
    }

    if (configs.pageType === 'prematch' || configs.showCompetitionsList) {
      const sportAlias =
        routeParams.params?.category || routeParams.params?.sport;

      const isNotRealSport = NOT_REAL_SPORTS.includes(sportAlias);
      const isFavTeam = sportAlias === ENotRealSports.team;

      if (isFavTeam) {
        return <SportsNavigation sportAlias={FAVORITE_TEAM_SPORT} key={-1} />;
      } else if (isNotRealSport) {
        return (
          <CategoriesNavigation
            category={sportAlias as TPrematchCategoryTypes}
            key={sportAlias}
          />
        );
      } else {
        return <SportsNavigation sportAlias={sportAlias} key={sportAlias} />;
      }
    } else {
      return (
        <SportsNavigation
          sportAlias={routeParams.params?.sport}
          key={routeParams.params?.sport}
        />
      );
    }
  }, [
    isBoostedOdds,
    configs,
    routeParams.params?.sport,
    routeParams.params?.category
  ]);

  const isEmptySportData = sportsData === undefined;

  return (
    <>
      <FavoriteSideBarSearchSection
        showFavoriteTeam={
          !!pageConfig.favoriteTeam && configs.pageType === 'prematch'
        }
      />

      {showTopLeague ? <TopLeagueCompactMenuItemWithWrapper /> : null}

      {showTopMatches ? (
        <div className="compactViewWrapper">
          <TopMatchesCompactMenuItem />
        </div>
      ) : null}

      {showOlympicCompetition ? (
        <div className="compactViewWrapper">
          <OlympicCompetitionCompactLayout />
        </div>
      ) : null}

      {isEmptySportData ? null : (
        <SportsbookNavigationsBlock heightAuto={configs.easyAccessAvailable}>
          {configs.easyAccessAvailable && (
            <SportsbookEasyAccessNavigation
              configs={{
                pageType: configs.pageType,
                outterRoutes: configs.routes,
                category: configs.category ? `${configs.category}` : 'sport'
              }}
            />
          )}
          <ConditionalWrapper
            condition={configs.pageType === 'prematch'}
            wrapper={children => {
              return (
                <div className="prematchNavigationWrapper">
                  <SportsHeader
                    externalTitle={t('sportsbook.prematch')}
                    sportAlias={
                      SpringConfigs.MOCKED_DATA
                        ? 'soccer'
                        : routeParams?.params?.category ||
                          routeParams?.params?.sport
                    }
                  />
                  {children}
                </div>
              );
            }}
          >
            {NavigationBlock}
          </ConditionalWrapper>
        </SportsbookNavigationsBlock>
      )}
    </>
  );
};

export const SportsbookLeftSideBar: ReturnType<typeof withRouter> | any =
  withRouter(_SportsbookLeftSideBar as any);
